import React, { useEffect, useMemo, useState } from "react";
import { useVault } from "../../contexts/VaultContext";
import { useFeedback } from "../../contexts/FeedbackContext";
import { Link } from "react-router-dom";
import { ROUTES } from "../../lib/nav";
import WidgetHeader from "../Common/WidgetHeader";
import { useTheme } from "../../core/theme/ThemeProvider";

/**
 * JournalWidget
 * - Persists to Vault key: "journal" (same as main Journal page)
 * - Writes to the user's selected folder (Drive/OPFS) via VaultProvider
 * - Supports tags/categories for organization
 */
const todayKey = (d = new Date()) => d.toISOString().slice(0, 10);

export default function JournalWidget({ maxVisible = 5, isDark }) {
  const { ready, getJSON, setJSON } = useVault();
  const { play } = useFeedback();
  const { mode } = useTheme();
  const [entries, setEntries] = useState([]);
  const [draft, setDraft] = useState("");
  const [tags, setTags] = useState("");
  const primaryBtn =
    "px-3 py-1 rounded text-sm font-semibold transition bg-[var(--mudo-primary)] text-[var(--mudo-onPrimary,#0b0b0b)] hover:brightness-110";
  const cardPanel = "rounded-lg border border-white/10 bg-slate-800/60";
  const textMain = "text-slate-100";
  const textSoft = "text-slate-300";

  // Load on mount / restore
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    (async () => {
      const data = await getJSON("journal", []);
      if (alive) setEntries(Array.isArray(data) ? data : []);
    })();
    const onRestored = async () => {
      const data = await getJSON("journal", []);
      setEntries(Array.isArray(data) ? data : []);
    };
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:journal-updated", onRestored);
    return () => {
      alive = false;
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:journal-updated", onRestored);
    };
  }, [ready, getJSON]);

  const addEntry = () => {
    const text = draft.trim();
    if (!text) return;
    const entry = {
      id: crypto.randomUUID?.() || `jr_${Date.now()}`,
      date: todayKey(),
      createdAt: new Date().toISOString(),
      text: text,
      tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
      pinned: false,
    };
    const next = [entry, ...entries];
    // Update UI immediately
    setEntries(next);
    setDraft("");
    setTags("");
    play?.("success");
    // Save to vault in background
    setJSON("journal", next);
    // Notify main journal page and streak system
    window.dispatchEvent(new Event("mudo:journal-updated"));
    window.dispatchEvent(new CustomEvent("mudo:journal-entry-created", { detail: { id: entry.id, date: entry.date } }));
  };

  // Only show the most recent entry (last one added) in widget
  const recent = useMemo(() => entries.slice(0, 1), [entries]);

  return (
    <div className="h-full w-full text-white">
      <div className="bg-slate-900/60 rounded-lg p-4 border border-white/10">
        <WidgetHeader
          title="Journal"
          right={<Link to={ROUTES.JOURNAL} className="text-sm underline opacity-80 text-slate-200">Open</Link>}
          className="mb-2"
        />

        {/* Draft box */}
        <div className={`${cardPanel} p-3 mb-3`}>
          <textarea
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Jot a quick idea…"
            className="w-full bg-transparent outline-none resize-y text-sm text-slate-100"
            style={{
              fontFamily: '"Caveat", "Handlee", ui-rounded, system-ui, sans-serif',
              fontSize: 16,
            }}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "enter") addEntry();
            }}
          />
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Tags (comma-separated, e.g. glaze, idea)"
            className="w-full bg-transparent outline-none text-xs mb-2 px-1 py-1 text-slate-300 border-t border-white/10"
          />
          <div className="flex justify-end">
            <button
              onClick={addEntry}
              className={primaryBtn}
              title="Save (Ctrl/Cmd+Enter)"
            >
              Save
            </button>
          </div>
        </div>

        {/* Recent notes */}
        <div className="space-y-2">
          {recent.length === 0 ? (
            <div className="text-xs text-slate-400">No notes yet.</div>
          ) : (
            recent.map((e) => (
              <div
                key={e.id}
                className={`${cardPanel} p-2 text-sm`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <div className="text-[11px] text-slate-400">
                    {new Date(e.createdAt || e.ts).toLocaleString()}
                  </div>
                  {e.pinned && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-black">📌</span>
                  )}
                </div>
                {!!(e.tags || []).length && (
                  <div className="text-[10px] mb-1 text-slate-400">
                    {(e.tags || []).map((t) => `#${t}`).join(" ")}
                  </div>
                )}
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    fontFamily: '"Caveat","Handlee",ui-rounded,system-ui,sans-serif',
                    fontSize: 16,
                  }}
                  className="text-slate-100"
                >
                  {e.text}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Load handwriting font once */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;600&family=Handlee&display=swap"
      />
    </div>
  );
}
